import React, { useState } from "react";
import { Box, TextField, Button, Typography, Alert, Link, Stack } from "@mui/material";
import { useSearchParams, useLocation } from "react-router-dom";
import api from "../services/api";
import { useTranslation } from "react-i18next";
import { withCountryPrefix } from "./utils/routeHelpers";

const ResetPassword = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const location = useLocation();
  const match = location.pathname.match(/^\/([a-z]{2})\b/);
  const country = match ? match[1] : "el";
  const loginPath = withCountryPrefix("/login", country);

  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSuccess(false);

    if (!password || !repeat) {
      setError(t("fill_all_fields") || "Please fill all fields.");
      return;
    }
    if (password !== repeat) {
      setError(t("passwords_no_match") || "Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await api.users.confirmPasswordReset(token, password);
      setSuccess(true);
      setPassword("");
      setRepeat("");
    } catch (err) {
      const backendError = err?.response?.data?.error;
      if (typeof backendError === "string" && backendError.toLowerCase().includes("password")) {
        setError(backendError);
      } else {
        setError(
          t("invalid_link") ||
          "This reset link is invalid or expired. Please request a new password reset link."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Box sx={{ mt: 8, maxWidth: 460, mx: "auto", px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("invalid_link") || "This reset link is invalid or expired."}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          {t("reset_instruction") || "Please request a new password reset link."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 8, maxWidth: 420, mx: "auto", px: 2 }}>
      <Typography variant="h4" gutterBottom>
        {t("reset_password") || "Reset Password"}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("success") || "Your password has been reset successfully."}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!success && (
      <form onSubmit={handleSubmit}>
        <TextField
          label={t("new_password")}
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          fullWidth
          required
          disabled={submitting}
          sx={{ mb: 2 }}
        />
        <TextField
          label={t("repeat_password")}
          type="password"
          value={repeat}
          onChange={e => setRepeat(e.target.value)}
          fullWidth
          required
          disabled={submitting}
          sx={{ mb: 2 }}
        />
        <Button type="submit" variant="contained" color="primary" fullWidth disabled={submitting}>
          {submitting ? (t("sending") || "Submitting...") : (t("set_new") || "Set new password")}
        </Button>
      </form>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Link href={loginPath} underline="hover">
          {t("login_btn") || "Login"}
        </Link>
        {!success && (
          <Typography variant="body2" color="text.secondary">
            {t("or_login_with_email") || "Need a new link? Go to login and click Forgot password."}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ResetPassword;
